import { isTokenExpired } from '@/auth/tokens'
import { createAccess } from '@/database/access'
import type { ErrorMessage } from '@/database/error'
import { stateMethodsFactory } from '@/database/mongobase'
import {
  createSocket,
  type SocketEventHandler,
  SubscriptionTypes,
} from '@/database/socket'
import { SubscriptionAction, subscriberFactory } from '@/database/subscribe'

export * from '@/database/utility'
export { SubscriptionAction, SubscriptionTypes }

/** Return realtime database access methods */
export function initStream(socketUrl: string, platformVersion?: string) {
  if (!socketUrl) throw new Error('Invalid URL provided to initStream')

  // Initialize socket instances
  const socket = createSocket(socketUrl, platformVersion)

  return {
    socket,
    updateAccessToken: socket.updateSocketToken,
    pauseDatabase: socket.pauseDatabase,
    ...subscriberFactory(socket),
  }
}

/** Return direct database access methods */
export function initDatabase(baseUrl: string) {
  if (!baseUrl) throw new Error('Invalid URL provided to initDatabase')

  return createAccess(baseUrl)
}

export type AppOptions = {
  healthUrl: string
  platformVersion?: string
  storageKey: string
  syncKey: string
  useImmutableUpdates: boolean
  tokenPrefix: string
  errorHandler?: SocketEventHandler<ErrorMessage>
}

/**
 * MONGOBASE (lite)
 *
 * Meta interface that wraps lower level access and subscribe methods
 * into a managed stage interface with offline persistence
 */
export function initApp(
  socketUrl: string,
  baseUrl: string,
  config: Partial<AppOptions> = {
    healthUrl: `${baseUrl}/ping-auth`,
    useImmutableUpdates: false,
    storageKey: 'app-storage',
    syncKey: 'pending-updates',
    tokenPrefix: 'app',
  },
) {
  if (!baseUrl || !socketUrl) throw new Error('Invalid URL passed to initApp')

  // Setup default config options
  const options = {
    healthUrl: `${baseUrl}/ping-auth`,
    useImmutableUpdates: false,
    storageKey: 'app-storage',
    syncKey: 'pending-updates',
    tokenPrefix: 'app',
    ...config,
  }

  // Initialize socket instances
  const socket = createSocket(
    socketUrl,
    config.platformVersion,
    config.errorHandler,
  )
  const access = createAccess(baseUrl)
  const { startConnectionPolling, ...mongobase } = stateMethodsFactory(
    access,
    socket,
    options,
  )

  // Clear pending and local data if the refresh token is expired.
  const refreshToken =
    localStorage.getItem(`${options.tokenPrefix}-refresh-token`) || ''

  const tokenExpired = !refreshToken || isTokenExpired(refreshToken)

  if (tokenExpired) {
    localStorage.removeItem(options.storageKey)
    localStorage.removeItem(options.syncKey)
  }

  // Poll at least once on app start if there are pending updates from a previous session
  if (localStorage.getItem(options.syncKey)) {
    startConnectionPolling()
  }

  return {
    socket,
    access,
    updateAccessToken: (accessToken: string) => {
      socket.updateSocketToken(accessToken)
      access.updateAccessToken(accessToken)
    },
    pauseDatabase: socket.pauseDatabase,
    ...mongobase,
  }
}
